import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutGrid, FileText, PenLine, Pencil,
  Search, Users, X, Menu, ChevronRight,
} from "lucide-react";

import logo from '../../assets/app/logo.png';
import EPages from "./EPages";
import EPost from "./EPost";
import SocialHandles from "./SocialHandles";
import ECategory from "./ECategory";
import AllPosts from "./EDIT/POSTS/AllPosts";
import AllPages from "./EDIT/PAGES/AllPages";


/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
   Desktop  → white page, black sidebar
   Mobile   → white page, black bottom bar
───────────────────────────────────────────────────────── */
const C = {
  paper:   "#ffffff",
  surface: "#f7f7f7",
  ink:     "#0a0a0a",
  inkMid:  "#6b6b6b",
  inkLight:"#a3a3a3",
  border:  "#e0e0e0",
  serif:   "'Cormorant Garamond', Georgia, serif",
  sans:    "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   TAB DEFINITIONS
───────────────────────────────────────────────────────── */
type Tab = "category" | "pages" | "post" | "edit" | "editpages" | "seo" | "social";

const TABS: { id: Tab; label: string; icon: React.ReactNode; short: string }[] = [
  { id:"category", label:"Categories", short:"Category", icon:<LayoutGrid size={18} strokeWidth={1.5}/> },
  { id:"pages",    label:"Pages",      short:"Pages",    icon:<FileText   size={18} strokeWidth={1.5}/> },
  { id:"post",     label:"New Post",   short:"Post",     icon:<PenLine    size={18} strokeWidth={1.5}/> },
  { id:"edit",     label:"Edit Posts", short:"Edit Posts",     icon:<Pencil     size={18} strokeWidth={1.5}/> },
    { id:"editpages",     label:"Edit Pages", short:"Edit Pages",     icon:<Pencil     size={18} strokeWidth={1.5}/> },

  { id:"seo",      label:"SEO",        short:"SEO",      icon:<Search     size={18} strokeWidth={1.5}/> },
  { id:"social",   label:"Social",     short:"Social",   icon:<Users      size={18} strokeWidth={1.5}/> },
];

/* ─────────────────────────────────────────────────────────
   CONTENT ROUTER
───────────────────────────────────────────────────────── */
const renderContent = (tab: Tab) => {
  switch (tab) {
    case "category": return <ECategory/>;
    case "pages":    return <EPages/>;
    case "post":     return <EPost/>;
    case "edit":     return <AllPosts/>;
        case "editpages":     return <AllPages/>;

    case "social":   return <SocialHandles/>;
    default:         return <ECategory/>;
  }
};

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const Editorial: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("category");
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 900);

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const activeTabDef = TABS.find(t => t.id === activeTab)!;

  return (
    <div style={{ minHeight:"100vh", background:C.paper, fontFamily:C.sans, color:C.ink, overflowX:"hidden" }}>
      <style>{FONT_CSS + SHELL_CSS}</style>

      {/* ══════════════════════════════
          DESKTOP LAYOUT
      ══════════════════════════════ */}
      {isDesktop ? (
        <div style={S.desktopWrap}>

          {/* ── SIDEBAR ── */}
          <aside style={S.sidebar}>
            {/* Brand */}
            <div style={S.sideTop}>
              <img src={logo} alt="Logo" style={S.sideLogo}/>
              <div style={S.sideRule}/>
              <p style={S.sideBrand}>EDITORIAL</p>
              <p style={S.sideSub}>CMS</p>
            </div>

            {/* Nav items */}
            <nav style={S.sideNav}>
              {TABS.map(tab => {
                const active = activeTab === tab.id;
                return (
                  <motion.button
                    key={tab.id}
                    whileHover={{ x: active ? 0 : 4 } as any}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      ...S.sideItem,
                      background: active ? "rgba(255,255,255,0.08)" : "transparent",
                      borderLeft: `2px solid ${active ? "#ffffff" : "transparent"}`,
                    }}
                    className="ed-nav-item"
                  >
                    <span style={{ color: active ? "#fff" : "rgba(255,255,255,0.4)", transition:"color 0.2s", display:"flex" }}>
                      {tab.icon}
                    </span>
                    <span style={{
                      fontSize:11, fontWeight: active ? 600 : 400,
                      letterSpacing:"0.1em",
                      color: active ? "#fff" : "rgba(255,255,255,0.45)",
                      transition:"color 0.2s",
                    }}>
                      {tab.label.toUpperCase()}
                    </span>
                    {active && (
                      <ChevronRight size={12} color="rgba(255,255,255,0.5)" style={{ marginLeft:"auto" }}/>
                    )}
                  </motion.button>
                );
              })}
            </nav>

            {/* Sidebar footer */}
            <div style={S.sideFooter}>
              <div style={{ height:1, background:"rgba(255,255,255,0.1)", marginBottom:16 }}/>
              <p style={{ fontSize:9, color:"rgba(255,255,255,0.25)", letterSpacing:"0.2em" }}>
                © {new Date().getFullYear()} 4REVA GROUP
              </p>
            </div>
          </aside>

          {/* ── MAIN CONTENT ── */}
          <main style={S.desktopMain}>
            {/* Breadcrumb bar */}
            <div style={S.breadcrumb}>
              <span style={S.breadcrumbRoot}>Editorial</span>
              <ChevronRight size={12} color={C.inkLight}/>
              <span style={S.breadcrumbActive}>{activeTabDef.label}</span>
            </div>

            {/* Page transition */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity:0, y:10 }}
                animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-6 }}
                transition={{ duration:0.22 }}
              >
                {renderContent(activeTab)}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>

      ) : (
        /* ══════════════════════════════
            MOBILE LAYOUT
        ══════════════════════════════ */
        <div style={S.mobileWrap}>

          {/* ── MOBILE HEADER ── */}
          <header style={S.mobileHeader}>
            <img src={logo} alt="Logo" style={S.mobileLogo}/>

            {/* Active tab label centred */}
            <div style={{ position:"absolute", left:"50%", transform:"translateX(-50%)" }}>
              <span style={S.mobileTitle}>{activeTabDef.label.toUpperCase()}</span>
            </div>

            {/* Hamburger → fullscreen menu */}
            <button onClick={() => setMenuOpen(true)} style={S.mobileMenuBtn}>
              <Menu size={22} color={C.ink} strokeWidth={1.5}/>
            </button>
          </header>

          {/* ── FULLSCREEN MENU ── */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity:0 }}
                animate={{ opacity:1 }}
                exit={{ opacity:0 }}
                style={S.overlay}
                onClick={() => setMenuOpen(false)}
              >
                <motion.div
                  initial={{ x:"100%" }}
                  animate={{ x:0 }}
                  exit={{ x:"100%" }}
                  transition={{ ease:[0.4,0,0.2,1], duration:0.3 }}
                  style={S.drawer}
                  onClick={e => e.stopPropagation()}
                >
                  {/* Drawer header */}
                  <div style={S.drawerHead}>
                    <div>
                      <div style={{ fontFamily:C.serif, fontSize:22, fontWeight:600, color:"#fff", letterSpacing:"0.06em" }}>EDITORIAL</div>
                      <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", letterSpacing:"0.3em", marginTop:3 }}>CMS DASHBOARD</div>
                    </div>
                    <button onClick={() => setMenuOpen(false)} style={S.drawerClose}>
                      <X size={18} color="rgba(255,255,255,0.7)"/>
                    </button>
                  </div>

                  <div style={{ height:1, background:"rgba(255,255,255,0.1)", margin:"4px 0 20px" }}/>

                  {/* Menu items */}
                  {TABS.map((tab, i) => {
                    const active = activeTab === tab.id;
                    return (
                      <motion.button
                        key={tab.id}
                        initial={{ opacity:0, x:12 }}
                        animate={{ opacity:1, x:0 }}
                        transition={{ delay: i * 0.05 }}
                        onClick={() => { setActiveTab(tab.id); setMenuOpen(false); }}
                        style={{
                          ...S.drawerItem,
                          background: active ? "rgba(255,255,255,0.08)" : "transparent",
                          borderLeft: `2px solid ${active ? "#fff" : "transparent"}`,
                        }}
                      >
                        <span style={{ color: active ? "#fff" : "rgba(255,255,255,0.4)", display:"flex" }}>
                          {tab.icon}
                        </span>
                        <span style={{ fontSize:14, color: active ? "#fff" : "rgba(255,255,255,0.55)", fontWeight: active ? 600 : 300, letterSpacing:"0.05em" }}>
                          {tab.label}
                        </span>
                        {active && <div style={{ width:4, height:4, borderRadius:"50%", background:"#fff", marginLeft:"auto" }}/>}
                      </motion.button>
                    );
                  })}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── MOBILE CONTENT ── */}
          <main style={S.mobileContent}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity:0, y:8 }}
                animate={{ opacity:1, y:0 }}
                exit={{ opacity:0, y:-6 }}
                transition={{ duration:0.2 }}
              >
                {renderContent(activeTab)}
              </motion.div>
            </AnimatePresence>
          </main>

          {/* ── BOTTOM TAB BAR ── */}
          <nav style={S.bottomBar}>
            {TABS.map(tab => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    ...S.bottomTab,
                    color: active ? C.ink : C.inkLight,
                    borderTop: `2px solid ${active ? C.ink : "transparent"}`,
                  }}
                >
                  <span style={{ display:"flex", marginBottom:3 }}>{tab.icon}</span>
                  <span style={{ fontSize:9, fontWeight: active ? 700 : 400, letterSpacing:"0.08em" }}>
                    {tab.short.toUpperCase()}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      )}
    </div>
  );
};

export default Editorial;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const SIDEBAR_W = 220;
const HEADER_H  = 56;
const TABS_H    = 58;

const S: { [key: string]: React.CSSProperties } = {

  /* ── Desktop ── */
  desktopWrap: {
    display: "grid",
    gridTemplateColumns: `${SIDEBAR_W}px 1fr`,
    minHeight: "100vh",
  },

  sidebar: {
    background: C.ink,
    position: "fixed",
    top: 0, left: 0,
    width: SIDEBAR_W,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    zIndex: 100,
    overflowY: "auto",
  },
  sideTop: {
    padding: "28px 24px 20px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
    flexShrink: 0,
  },
  sideLogo:  { height: 32, objectFit:"contain", display:"block", marginBottom:18 },
  sideRule:  { height:1, background:"rgba(255,255,255,0.12)", marginBottom:14 },
  sideBrand: { fontSize:14, fontFamily:C.serif, fontWeight:600, color:"#fff", letterSpacing:"0.22em", margin:0 },
  sideSub:   { fontSize:8, color:"rgba(255,255,255,0.3)", letterSpacing:"0.4em", marginTop:3 },

  sideNav: {
    flex: 1, display:"flex", flexDirection:"column",
    padding: "16px 0", gap: 2,
  },
  sideItem: {
    display: "flex", alignItems:"center", gap:12,
    padding: "11px 24px",
    background: "transparent", border:"none",
    cursor: "pointer", width:"100%", textAlign:"left",
    fontFamily: C.sans, transition:"all 0.18s",
    borderLeftWidth: 2, borderLeftStyle:"solid",
  },
  sideFooter: { padding:"16px 24px", flexShrink:0 },

  desktopMain: {
    marginLeft: SIDEBAR_W,
    minHeight: "100vh",
    background: C.paper,
  },
  breadcrumb: {
    display: "flex", alignItems:"center", gap:8,
    padding: "14px 32px",
    borderBottom: `1px solid ${C.border}`,
    background: C.paper,
    position: "sticky", top:0, zIndex:50,
  },
  breadcrumbRoot:  { fontSize:10, color:C.inkLight, letterSpacing:"0.12em", fontFamily:C.sans },
  breadcrumbActive:{ fontSize:10, color:C.ink,      letterSpacing:"0.12em", fontFamily:C.sans, fontWeight:600 },

  /* ── Mobile ── */
  mobileWrap: { display:"flex", flexDirection:"column", minHeight:"100vh" },

  mobileHeader: {
    position: "fixed", top:0, left:0, right:0, zIndex:100,
    height: HEADER_H,
    background: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(14px)",
    borderBottom: `1px solid ${C.border}`,
    display: "flex", alignItems:"center",
    justifyContent: "space-between",
    padding: "0 18px",
  },
  mobileLogo:    { height:28, objectFit:"contain" },
  mobileTitle:   { fontSize:10, fontWeight:700, letterSpacing:"0.22em", color:C.ink, fontFamily:C.sans },
  mobileMenuBtn: { background:"none", border:"none", cursor:"pointer", padding:4, display:"flex" },

  mobileContent: {
    marginTop: HEADER_H,
    marginBottom: TABS_H,
    flex: 1,
    background: C.paper,
  },

  /* Drawer / overlay */
  overlay: {
    position: "fixed", inset:0, zIndex:500,
    background: "rgba(0,0,0,0.5)",
    backdropFilter: "blur(3px)",
  },
  drawer: {
    position: "absolute", top:0, right:0, bottom:0,
    width: 280,
    background: C.ink,
    padding: "28px 24px 40px",
    display: "flex", flexDirection:"column",
    overflowY: "auto",
  },
  drawerHead: {
    display: "flex", alignItems:"flex-start",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  drawerClose: {
    background: "none", border:`1px solid rgba(255,255,255,0.15)`,
    width:32, height:32, cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"center",
    flexShrink:0,
  },
  drawerItem: {
    display: "flex", alignItems:"center", gap:14,
    padding: "14px 16px",
    background: "transparent", border:"none",
    cursor: "pointer", width:"100%", textAlign:"left",
    fontFamily: C.sans, borderLeftWidth:2, borderLeftStyle:"solid",
    marginBottom: 2,
  },

  /* Bottom tab bar */
  bottomBar: {
    position: "fixed", bottom:0, left:0, right:0,
    height: TABS_H,
    background: "rgba(255,255,255,0.97)",
    backdropFilter: "blur(14px)",
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    zIndex: 100,
  },
  bottomTab: {
    flex: 1, display:"flex", flexDirection:"column",
    alignItems:"center", justifyContent:"center",
    background: "transparent", border:"none",
    cursor:"pointer", fontFamily:C.sans,
    transition: "color 0.18s, border-top 0.18s",
    borderTopWidth: 2, borderTopStyle:"solid",
    paddingTop: 6,
  },
};

/* ─────────────────────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────────────────────── */
const SHELL_CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::-webkit-scrollbar { width: 0; }

  .ed-nav-item:hover span:first-child { color: rgba(255,255,255,0.8) !important; }
  .ed-nav-item:hover span:nth-child(2) { color: rgba(255,255,255,0.7) !important; }
`;